import React, { useEffect, useState } from 'react';
import { Card, CardContent, Typography } from '@material-ui/core';
import { Table, TableColumn, Link, Progress, ErrorPanel } from '@backstage/core-components';
import { useApi } from '@backstage/core-plugin-api';
import { devOpsApiRef, ReleaseHistory } from '@internal/backstage-plugin-ewii-azure';
import { useEntity } from '@backstage/plugin-catalog-react';
import { Entity } from '@backstage/catalog-model';
import { getBuildDateFromDate } from './CatalogHelpers';
import { parseLocationRef } from '@backstage/catalog-model';
import { ChangeLogModal } from '../../../plugins/release-overview/components/ChangeLogModal';
import { extractBaseFileName } from '../../../plugins/release-overview/utils';

export type EntityReleaseHistoryCardProps = {
  variant?: 'gridItem' | string;
};

const RELEASE_HISTORY_ANNOTATION = 'ewii/release-history-url';

const getCommitUrl = (entity: Entity, release: ReleaseHistory): string => {
  const repoLocation = entity.metadata.annotations?.['backstage.io/source-location'];
  if(!repoLocation) {
    return '';
  }
  const repoUrl = parseLocationRef(repoLocation).target;

  const {fromBranch, toBranch, fromCommit, toCommit} = release;
  if(fromBranch == toBranch) {
    return `${repoUrl}/branchCompare?baseVersion=GC${release.fromCommit}&targetVersion=GC${release.toCommit}&_a=files`.trim();
  }
  return `${repoUrl}/branchCompare?baseVersion=GB${release.fromBranch}&targetVersion=GB${release.toBranch}&_a=files`.trim();
}

const getBranchUrl = (entity: Entity, branchName: string, commitId: string): string => {
  const repoLocation = entity.metadata.annotations?.['backstage.io/source-location'];
  if(!repoLocation) {
    return '';
  }
  const repoUrl = parseLocationRef(repoLocation).target;
  if(branchName == 'main') {
    return `${repoUrl}?version=GC${commitId}`;
  }
  return `${repoUrl}?version=GB${branchName}`;
}

export const EntityReleaseHistoryCard: React.FC<EntityReleaseHistoryCardProps> = ({ variant }) => {
  const { entity } = useEntity();
  const releaseHistoryUrl = entity?.metadata?.annotations?.[RELEASE_HISTORY_ANNOTATION];
  const devOpsApi = useApi(devOpsApiRef) as { getReleaseHistory: (component: string) => Promise<ReleaseHistory[]> };
  const [history, setHistory] = useState<ReleaseHistory[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);


  useEffect(() => {
    if (!releaseHistoryUrl) {
      setHistory([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    devOpsApi.getReleaseHistory(entity.metadata.name)
      .then((data: ReleaseHistory[]) => {
        setHistory([...data].sort((a, b) => (b.releaseDate.getTime() - a.releaseDate.getTime())));
        setLoading(false);
      })
      .catch((e: unknown) => {
        setError(e instanceof Error ? e : new Error('Failed to fetch release history'));
        setLoading(false);
      });
  }, [entity.metadata.name, devOpsApi]);

  const columns: TableColumn<ReleaseHistory>[] = [
    { title: 'Release Number', field: 'releaseNumber', render: row => row.releaseUrl ? <Link to={row.releaseUrl} target="_blank">{row.releaseNumber}</Link> : row.releaseNumber },
    { title: 'Release Date', field: 'releaseDate', render: row => getBuildDateFromDate(row.releaseDate) },
    { title: 'Status', field: 'status', render: row => (
      <span style={{ 
        color: row.status === 'deployed' ? '#28a745' : 
               row.status === 'staged' ? '#ffc107' : 
               row.status === 'cancelled' ? '#dc3545' : '#6c757d',
        fontWeight: 'bold',
        textTransform: 'capitalize'
      }}>
        {row.status}
      </span>
    ) },
    { title: 'Branch', field: 'toBranch', render: row => <Link to={getBranchUrl(entity, row.toBranch, row.toCommit)} target="_blank">{row.toBranch}</Link> },
    { title: 'Commit', field: 'toCommit', render: row => <Link to={getCommitUrl(entity, row)} target="_blank">{row.toCommit.slice(0, 7)}</Link>   },
    { title: 'Change Log', field: 'changeLogUrl', render: row => {
      let fileName = '';
      if (row.changeLogFile) {
        fileName = extractBaseFileName(row.changeLogFile);
      } else if (row.changeLogUrl) {
        fileName = extractBaseFileName(row.changeLogUrl);
      }
      return (
        <ChangeLogModal 
          component={entity.metadata.name} 
          fileName={fileName}
          displayText="View"
          fullRowExpansion={true}
        />
      );
    } },
  ];

  return (
    <Card variant={variant === 'gridItem' ? 'outlined' : undefined}>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Release History
        </Typography>
        {loading && <Progress />}
        {error && <ErrorPanel error={error} />}
        {!loading && !error && (
          <Table
            title=""
            options={{ paging: false, search: false }}
            columns={columns}
            data={history}
          />
        )}
      </CardContent>
    </Card>
  );
}; 